import React, { useState, useEffect, useContext } from "react";
import * as cognito from "../libs/cognito";
import { useLoading } from "./loadingContext";
import { logoutFromAll } from "../hooks/useCognitoOAuth";

export const UserAuthStatus = {
  Loading: "Loading",
  SignedIn: "SignedIn",
  SignedOut: "SignedOut",
};

const defaultState = {
  sessionInfo: {},
  authStatus: UserAuthStatus.Loading,
};

export const AuthContext = React.createContext(defaultState);

export const AuthIsSignedIn = ({ children }) => {
  const { authStatus } = useContext(AuthContext);

  return <>{authStatus === UserAuthStatus.SignedIn ? children : null}</>;
};

export const AuthIsNotSignedIn = ({ children }) => {
  const { authStatus } = useContext(AuthContext);

  return <>{authStatus === UserAuthStatus.SignedOut ? children : null}</>;
};

const AuthProvider = ({ children }) => {
  const [authStatus, setAuthStatus] = useState(UserAuthStatus.Loading);
  const [sessionInfo, setSessionInfo] = useState({});
  const [attrInfo, setAttrInfo] = useState([]);
  const { setLoading } = useLoading();

  async function getSessionInfo() {
    try {
      const session = await getSession();
      setSessionInfo({
        accessToken: session.accessToken.jwtToken,
        refreshToken: session.refreshToken.token,
      });
      const { attributes, username } = await getAttributes();
      setAttrInfo(attributes);
      setSessionInfo((prev) => ({ ...prev, username }));
      setAuthStatus(UserAuthStatus.SignedIn);
    } catch (err) {
      setAuthStatus(UserAuthStatus.SignedOut);
    }
  }

  useEffect(() => {
    getSessionInfo();
  }, [setAuthStatus, setSessionInfo, authStatus]);

  if (authStatus === UserAuthStatus.Loading) {
    return null;
  }

  async function signInWithEmail(username, password) {
    try {
      setLoading(true);
      await cognito.signInWithEmail(username, password);
      setAuthStatus(UserAuthStatus.SignedIn);
    } catch (err) {
      setAuthStatus(UserAuthStatus.SignedOut);
      throw err;
    } finally {
      setLoading(false);
    }
  }

  async function deleteBySignInWithEmail(username, password) {
    try {
      setLoading(true);
      await cognito.signInWithEmail(username, password);
      getSessionInfo();
    } catch (err) {
      setAuthStatus(UserAuthStatus.SignedOut);
      throw err;
    } finally {
      setLoading(false);
    }
  }

  async function authenticateUser(access_token, id_token, refresh_token) {
    try {
      setLoading(true);
      await cognito.authenticateUser(access_token, id_token, refresh_token);
      setAuthStatus(UserAuthStatus.SignedIn);
    } catch (err) {
      setAuthStatus(UserAuthStatus.SignedOut);
      throw err;
    } finally {
      setLoading(false);
    }
  }

  async function refreshSession() {
    const session = await getSession();
    if (!session.isValid()) {
      console.log("session is valid");
    } else {
      const refresh_token = session.getRefreshToken();
      try {
        await cognito.refreshSession(refresh_token);
      } catch (err) {
        console.log(err);
      }
    }
  }

  async function getAttributeVerificationCode(attrName) {
    try {
      setLoading(true);
      const result = await cognito.getAttributeVerificationCode(attrName);
      return result;
    } catch (err) {
      console.error(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }

  async function verifyAttribute(confirmationCode, attrName) {
    try {
      setLoading(true);
      await cognito.verifyAttribute(confirmationCode, attrName);
    } catch (err) {
      console.error(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }

  async function signUpWithEmail(payload) {
    try {
      setLoading(true);
      const result = await cognito.signUpUserWithEmail(
        payload.username,
        payload.email,
        payload.given_name,
        payload.family_name,
        payload.phone_number,
        payload.password,
        payload.country,
        payload.content_type,
        payload.version,
        payload.content_type_loyalty,
        payload.version_loyalty,
        payload.registration_method
      );
      return result;
    } catch (err) {
      console.error(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }

  async function signOut(isNavigate = true) {
    try {
      setLoading(true);
      await new Promise((resolve, reject) => {
        cognito.signOut((err) => {
          if (err) return reject(err);
          resolve();
        });
      });
      logoutFromAll(isNavigate);
      setAttrInfo([]);
      setSessionInfo({});
      setAuthStatus(UserAuthStatus.SignedOut);
    } catch (err) {
      console.error(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }

  async function verifyCode(username, code) {
    try {
      setLoading(true);
      await cognito.verifyCode(username, code);
    } catch (err) {
      console.error(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }

  async function getSession() {
    const session = await cognito.getSession();
    return session;
  }

  async function getAttributes() {
    const attr = await cognito.getAttributes();
    return attr;
  }

  async function setAttribute(attr) {
    try {
      setLoading(true);
      const res = await cognito.setAttribute(attr);
      return res;
    } catch (err) {
      console.error(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }

  async function setProfileAttribute(family_name, given_name) {
    try {
      setLoading(true);
      const res = await cognito.setProfileAttribute(family_name, given_name);
      return res;
    } catch (err) {
      console.error(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }

  async function sendCode(username) {
    try {
      setLoading(true);
      await cognito.sendCode(username);
    } catch (err) {
      console.error(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }

  async function forgotPassword(username, code, password) {
    try {
      setLoading(true);
      await cognito.forgotPassword(username, code, password);
    } catch (err) {
      console.error(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }

  async function changePassword(oldPassword, newPassword) {
    try {
      setLoading(true);
      await cognito.changePassword(oldPassword, newPassword);
    } catch (err) {
      console.error(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }

  async function initiateAuth(username) {
    try {
      setLoading(true);
      const result = await cognito.initiateAuth(username);
      return result;
    } catch (err) {
      console.error(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }

  async function answerChallenge(code) {
    try {
      setLoading(true);
      const result = await cognito.answerChallenge(code);
      return result;
    } catch (err) {
      console.error(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }

  const state = {
    authStatus,
    sessionInfo,
    attrInfo,
    authenticateUser,
    signUpWithEmail,
    signInWithEmail,
    deleteBySignInWithEmail,
    refreshSession,
    signOut,
    verifyCode,
    getSession,
    sendCode,
    forgotPassword,
    changePassword,
    getAttributes,
    setAttribute,
    initiateAuth,
    answerChallenge,
    setProfileAttribute,
    getSessionInfo,
    getAttributeVerificationCode,
    verifyAttribute,
  };

  return <AuthContext.Provider value={state}>{children}</AuthContext.Provider>;
};

export default AuthProvider;
